/**
 * useViewportCulling.ts
 * AMÉLIORATION: Hook pour optimiser le rendu en ne rendant que les objets visibles dans le viewport
 * Améliore les performances avec beaucoup d'objets (100+)
 */
import { useMemo } from 'react';
import type { ShapeData } from '../types/ShapeTypes';

interface ViewportBounds {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

/**
 * Calcule quels shapes sont visibles dans le viewport
 */
export function useViewportCulling(
  shapes: ShapeData[],
  viewport: ViewportBounds | null,
  padding: number = 200 // Padding pour précharger les objets proches
): ShapeData[] {
  return useMemo(() => {
    if (!viewport || shapes.length === 0) {
      return shapes;
    }

    // Si on a peu d'objets, pas besoin de culling
    if (shapes.length < 50) {
      return shapes;
    }

    // Calculer les bounds du viewport avec padding
    const viewportLeft = viewport.x - padding;
    const viewportRight = viewport.x + viewport.width + padding;
    const viewportTop = viewport.y - padding;
    const viewportBottom = viewport.y + viewport.height + padding;

    // Filtrer les shapes visibles
    return shapes.filter(shape => {
      const shapeLeft = shape.x || 0;
      const shapeRight = shapeLeft + (shape.width || 0);
      const shapeTop = shape.y || 0;
      const shapeBottom = shapeTop + (shape.height || 0);

      // Vérifier si le shape intersecte avec le viewport
      return !(
        shapeRight < viewportLeft ||
        shapeLeft > viewportRight ||
        shapeBottom < viewportTop ||
        shapeTop > viewportBottom
      );
    });
  }, [shapes, viewport, padding]);
}

/**
 * Calcule les bounds du viewport depuis un stage Konva
 */
export function getViewportBounds(
  stage: {
    x: () => number;
    y: () => number;
    width: () => number;
    height: () => number;
    scaleX: () => number;
  } | null
): ViewportBounds | null {
  if (!stage) return null;

  const stageX = stage.x();
  const stageY = stage.y();
  const stageWidth = stage.width();
  const stageHeight = stage.height();
  const scale = stage.scaleX();

  // Calculer le viewport en coordonnées canvas
  const viewportX = -stageX / scale;
  const viewportY = -stageY / scale;
  const viewportWidth = stageWidth / scale;
  const viewportHeight = stageHeight / scale;

  return {
    x: viewportX,
    y: viewportY,
    width: viewportWidth,
    height: viewportHeight,
    scale,
  };
}
